import store from '@/features/common/store';
import router from '@/router';
import { getPostalCodeValidationRules, validateMapRules } from '@/features/shipment/helpers/createShipment';
import { safeListedCountryCodes } from '@/features/common/raw';
import { requiredRules } from '@/features/common/helpers/validationRules';
import { ClarificationAddressDto } from '@/features/shipment/types';
import {
  FormFieldError,
  scrollToElement,
  findPriorityError as findPriorityErrorBase,
  validateAndScrollToError as validateAndScrollToErrorBase,
} from '@/features/common/helpers/scrollToError';
import { EstimateDataType } from '../types';
import { estimateFormFields } from '../constants';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getEstimateValidationRules = (
  estimateData: EstimateDataType,
) => {
  const {
    fromAddress, toAddress, signatureOptions,
  } = estimateData;

  const rules = new Map([
    ['fromAddress', [
      new Map([
        ['countryCode', {
          rule: [...requiredRules],
          value: fromAddress.countryCode,
        }],
        ['postalCode', {
          rule: [...getPostalCodeValidationRules(fromAddress.countryCode)],
          value: fromAddress.postalCode,
        }],
        ['city', {
          rule: !!fromAddress.countryCode
            && !safeListedCountryCodes.includes(fromAddress.countryCode)
            ? [...requiredRules] : [],
          value: fromAddress.city,
        }],
      ]),
    ]],
    ['toAddress', [
      new Map([
        ['countryCode', {
          rule: [...requiredRules],
          value: toAddress.countryCode,
        }],
        ['postalCode', {
          rule: [...getPostalCodeValidationRules(toAddress.countryCode)],
          value: toAddress.postalCode,
        }],
        ['city', {
          rule: !!toAddress.countryCode
            && !safeListedCountryCodes.includes(toAddress.countryCode)
            ? [...requiredRules] : [],
          value: toAddress.city,
        }],
      ]),
    ]],
    ['signatureOptions', {
      rule: [...requiredRules],
      value: signatureOptions,
    }],
  ]);

  return rules;
};

export const isValidEstimateData = (estimateData: EstimateDataType): {
  valid: boolean,
  key?: string,
} => {
  // console.log('estimate data: ', estimateData);
  if (!estimateData) return { valid: false };

  const {
    fromAddress, toAddress, packages,
  } = estimateData;

  if (!fromAddress || !toAddress) return { valid: false };
  if (!packages || !packages.length) return { valid: false };

  const rules = getEstimateValidationRules(estimateData);

  // console.log(rules);

  return validateMapRules(rules);
};

export const validateEstimateData = async (): Promise<boolean> => {
  await new Promise((resolve) => setTimeout(resolve, 1200));

  const estimateData = store.getters.getEstimateData as EstimateDataType;

  const { valid } = isValidEstimateData(estimateData);

  if (!valid) {
    router.push({
      name: 'estimate-get-estimate',
      query: { isValid: 'false' },
    });
    return false;
  }

  const shipDateTime = router.currentRoute.query.shipDateTime as string;

  await store.dispatch('getNewEstimateRates', { date: shipDateTime, showLoading: false });

  return true;
};

export { scrollToElement };

export function findPriorityError(errors: FormFieldError[]) {
  return findPriorityErrorBase(errors, estimateFormFields);
}

export function validateAndScrollToError(errors: FormFieldError[]): string | null {
  return validateAndScrollToErrorBase(errors, estimateFormFields);
}

export function loadEstimateDataFromLocalstorage(): {
  fromAddresses: ClarificationAddressDto | undefined;
  toAddresses: ClarificationAddressDto | undefined;
  } {
  const estimateAddressData = localStorage.getItem('estimateAddressData');

  if (!estimateAddressData) {
    return {
      fromAddresses: undefined,
      toAddresses: undefined,
    };
  }

  const existingData: {
          fromAddresses: ClarificationAddressDto | undefined;
          toAddresses: ClarificationAddressDto | undefined;
        } = JSON.parse(estimateAddressData) || {};

  return existingData;
}
